import React, { useState } from "react";
import {
  X,
  FileBadge,
  CircleDot,
  CheckCircle,
  XCircle,
  Loader,
} from "lucide-react";
import frameBg from "../../Assets/FrameBGDashboard.png";
import { ResumeTicket } from "../../Services/TicketService";
import NotificationAlert from "../Alerts/NotificationAlert";
import { useNavigate } from "react-router-dom";

const ResumeTicketModal = ({ show, onClose, ticketData, onResume }) => {
  const [resumeRemark, setResumeRemark] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!show || !ticketData) return null;

  // Call API inside modal
  const handleResume = async () => {
    try {
      setLoading(true);

      const payload = {
        ticketNo: ticketData.ticketNumber,
        remark: resumeRemark,
      };

      const response = await ResumeTicket(payload);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({
          type: "success",
          message: data.message,
        });
        onResume();
        setResumeRemark("");
        setTimeout(() => {
          onClose();
          setAlert(null);
        }, 4000);
      } else {
        setAlert({
          type: "error",
          message: data.message || "Resume failed",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.response?.data?.message || "Failed to load tickets.",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" }); // Jan, Feb, Mar...
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // convert 0 → 12

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-white  w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between text-white shadow-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold tracking-wide">
                Resume Ticket
              </h2>

              <span className="mt-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium w-fit">
                #{ticketData.ticketNumber}
              </span>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition"
            >
              <X size={26} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Ticket Details */}
            <div className="bg-white border shadow-sm rounded-xl p-5 space-y-4 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileBadge size={16} className="text-blue-600" />
                  Ticket Details
                </h3>

                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full
                    ${
                      ticketData.status === "New"
                        ? "bg-blue-100 text-blue-700"
                        : ticketData.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : ticketData.status === "Rejected"
                            ? "bg-red-100 text-red-700"
                            : ticketData.status === "In Progress"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {ticketData.status === "New" && <CircleDot size={14} />}
                    {ticketData.status === "Approved" && (
                      <CheckCircle size={14} />
                    )}
                    {ticketData.status === "Rejected" && <XCircle size={14} />}
                    {ticketData.status === "In Progress" && (
                      <Loader size={14} className="animate-spin" />
                    )}

                    {ticketData.status}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="grid grid-cols-1 gap-3 text-[0.93rem] text-gray-700">
                <p>
                  <span className="font-semibold">Title:</span>{" "}
                  {ticketData.title}
                </p>
                <p>
                  <span className="font-semibold">Raised By:</span>{" "}
                  {ticketData.raisedByEmail}
                </p>
                <p>
                  <span className="font-semibold">Project:</span>{" "}
                  {ticketData.projectName}
                </p>
                <p>
                  <span className="font-semibold">Raised At:</span>{" "}
                  {formatDateTime(ticketData.raisedAt)}
                </p>
                <p>
                  <span className="font-semibold text-gray-900">Severity:</span>{" "}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold w-fit
                  ${
                    ticketData.severity === "High"
                      ? "bg-red-100 text-red-700"
                      : ticketData.severity === "Moderate"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                  }`}
                  >
                    {ticketData.severity}
                  </span>
                </p>
              </div>
            </div>

            {/* Remarks */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks
              </label>

              <textarea
                rows={4}
                value={resumeRemark}
                onChange={(e) => setResumeRemark(e.target.value)}
                placeholder="Add any remarks related to Resume..."
                className="w-full p-3 border rounded-lg text-sm focus:outline-none 
                         focus:ring-2 focus:ring-blue-400 shadow-sm"
              ></textarea>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 items-center shadow-inner">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 shadow-sm transition"
            >
              Cancel
            </button>

            <button
              onClick={handleResume}
              disabled={loading}
              className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition"
            >
              {loading ? "Resuming ..." : "Resume Ticket"}
            </button>
          </div>
        </div>
        {alert && (
          <NotificationAlert
            type={alert.type}
            message={alert.message}
            show={true}
            duration={4000}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </>
  );
};

export default ResumeTicketModal;
